import { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import { useKeyboardEvents } from './hooks/useKeyboardEvents';

import { useEditorData, useEditorHistory } from './providers/EditorProvider';
import { useTooling } from './providers/EditorToolingProvider';
import { useSlideSave } from './providers/SaveProvider';
import { CANVAS_MARGIN, useHandleResize } from './hooks/useHandleReize';
import { useZoom } from './hooks/useZoom';
import styled from 'styled-components';
import { editorTheme } from '../../theme';
import { RealSize } from '../../types';
import MenuContext from '../../components/common/MenuContext';
import SelectionActions from '../../components/SelectionActions';
import { useInitControls } from '../../core/hooks/useInitControls';
import { useEventControls } from '../../core/hooks/useEventControls';
import { useLoadJSON } from '../../core/hooks/useLoadJSON';
import { useUpdateCanvas } from '../../core/hooks/useUpdateCanvas';
import { disposeWidgets } from './utils/common';
import { releaseVideos } from './utils/handleVideo';

export interface Props extends RealSize {
  onReady?: (canvas: fabric.Canvas) => void;
}

// The canvas is sized from this container (fitted, or zoomed and scrolled),
// so the container's size must come from the layout, never from the canvas
// inside it. Auto margins center the canvas and still let it scroll fully
// when it is larger than the container.
const Container = styled.div`
  position: absolute;
  inset: 0;
  overflow: auto;
  display: flex;
  padding: ${CANVAS_MARGIN}px;
  background: ${editorTheme.workspace};
  & > .canvas-container {
    margin: auto;
    flex: none;
  }
`;

// Wheel steps are small and frequent; this turns one into a zoom factor.
const WHEEL_ZOOM_SPEED = 0.01;

export const FabricJSCanvas = ({ onReady, realWidth, realHeight }: Props) => {
  const canvasEl = useRef(null);
  const canvasElParent = useRef<HTMLDivElement>(null);
  const [points, setPoints] = useState<common.PointsProps | null>(null);
  const { canvas: currentCanvas, initLoad, setInitLoad } = useEditorData();
  const { history } = useEditorHistory();
  const { initControls } = useInitControls();
  const { eventControls } = useEventControls();
  const { loadJSON } = useLoadJSON();
  const { updateCanvas } = useUpdateCanvas();

  const { openPreview } = useTooling();
  const { initialProjectData } = useSlideSave();

  useHandleResize(canvasElParent, { realWidth, realHeight }, openPreview);
  useKeyboardEvents();
  const { zoomTo } = useZoom();
  const zoomToRef = useRef(zoomTo);
  zoomToRef.current = zoomTo;

  // Ctrl/Cmd + wheel, and trackpad pinch (sent as a wheel with ctrlKey),
  // zoom around the pointer instead of zooming the page.
  useEffect(() => {
    const container = canvasElParent.current;
    if (!container) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const current = currentCanvas?.getZoom();
      if (!current) return;
      zoomToRef.current(current * Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED), {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [currentCanvas]);

  useEffect(() => {
    if (initialProjectData?.objects && currentCanvas && !initLoad) {
      setInitLoad(true);
      loadJSON(currentCanvas, initialProjectData);
    }
  }, [initialProjectData, currentCanvas]);

  useEffect(() => {
    const { present } = history;
    if (currentCanvas && initLoad) {
      updateCanvas(currentCanvas, present, history.isUndoRedo, history.isReset);
    }
  }, [history.present]);

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasEl.current, {
      fireRightClick: true,
    });
    initControls();
    eventControls(canvas, setPoints);
    if (onReady) {
      onReady(canvas);
    }
    return () => {
      disposeWidgets(canvas);
      releaseVideos(canvas);
      canvas.off();
      canvas.dispose();
    };
  }, []);

  return (
    <Container
      id="canvas-editor-container"
      onContextMenu={(e: any) => {
        e.preventDefault();
        if (e?.target?.localName === 'div') {
          setPoints(null);
        } else {
          setPoints({
            x: e.clientX,
            y: e.clientY,
          });
        }
      }}
      onMouseDown={(e: any) => {
        if (
          e?.target?.localName === 'div' &&
          currentCanvas &&
          e?.target?.id !== 'overlay'
        ) {
          currentCanvas.discardActiveObject();
          currentCanvas.requestRenderAll();
          setPoints(null);
        }
      }}
      ref={canvasElParent}
    >
      <canvas ref={canvasEl} />
      <SelectionActions onMore={setPoints} />
      {points && (
        <MenuContext
          points={points}
          handleClose={() => {
            setPoints(null);
          }}
        />
      )}
    </Container>
  );
};
